import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Copy, Pencil, ChevronDown, ChevronUp, Filter, X, Search, Plus, ShieldAlert, ClipboardList } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Money } from "../context/PrivacyContext";
import { todayISO, fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ConceptSelect from "../components/ConceptSelect";
import MemberSelect, { MemberBadge } from "../components/MemberSelect";
import MovementTrace from "../components/MovementTrace";
import MoneyMovementFields from "../components/MoneyMovementFields";

const EMPTY_FORM = { date: todayISO(), amount: "", category: "", subcategory: "", concept_id: "", provider_id: "", payment_method_id: "", account_id: "", comment: "", member_id: null };

function ProviderPicker({ value, onChange, providers, onCreate, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = providers.find((p) => p.id === value);
  const activeProviders = providers.filter((p) => p.active);
  const filtered = activeProviders.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const canCreate = q.trim() && !activeProviders.some((p) => p.name.trim().toLowerCase() === q.trim().toLowerCase());

  return (
    <div className="relative">
      <button
        type="button"
        data-testid={testId || "provider-picker"}
        onClick={() => setOpen((v) => !v)}
        className="w-full h-10 border border-input rounded-md px-3 text-sm text-left flex items-center justify-between hover:border-foreground/50 transition-colors"
      >
        <span className={selected ? "" : "text-muted-foreground"}>{selected ? selected.name : "Selecciona proveedor…"}</span>
        <ChevronDown size={14} className="text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 border border-border bg-background rounded-md shadow-lg max-h-64 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-border relative">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="provider-search" className="pl-8 h-8" placeholder="Buscar o escribir nuevo…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && !canCreate && <div className="text-xs text-muted-foreground p-3 text-center">Sin proveedores</div>}
            {filtered.map((p) => (
              <button key={p.id} type="button" data-testid={`provider-opt-${p.id}`} onClick={() => { onChange(p.id); setOpen(false); setQ(""); }} className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary ${p.id === value ? "bg-secondary" : ""}`}>{p.name}</button>
            ))}
            {canCreate && (
              <button
                type="button"
                data-testid="provider-create-inline"
                onClick={async () => {
                  try {
                    const r = await api.post("/providers", { name: q.trim(), active: true });
                    toast.success(`Proveedor "${r.data.name}" creado`);
                    onCreate?.(r.data);
                    onChange(r.data.id);
                    setOpen(false); setQ("");
                  } catch (e) { toast.error(apiError(e)); }
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-secondary border-t border-border flex items-center gap-2 text-primary font-semibold"
              >
                <Plus size={13} /> Crear &quot;{q.trim()}&quot;
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ExpenseForm({ form, setForm, cats, providers, methods, accounts, onProviderCreated, onSubmit, submitLabel, testPrefix }) {
  const disabled = !form.amount || parseFloat(String(form.amount).replace(",", ".")) <= 0 || (!form.concept_id && !form.category) || !form.payment_method_id;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Fecha</label>
          <Input data-testid={`${testPrefix}-date`} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Importe (€)</label>
          <Input data-testid={`${testPrefix}-amount`} inputMode="decimal" placeholder="0,00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="text-lg font-semibold tabular" />
        </div>
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Proveedor</label>
        <ProviderPicker value={form.provider_id} providers={providers} onCreate={onProviderCreated} onChange={(id) => setForm({ ...form, provider_id: id })} testId={`${testPrefix}-provider`} />
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Concepto (Catálogo Maestro) *</label>
        <ConceptSelect
          kind="gasto"
          value={form.concept_id}
          legacyLabel={!form.concept_id ? [form.category, form.subcategory].filter(Boolean).join(" · ") : ""}
          onChange={(id) => setForm({ ...form, concept_id: id || "" })}
          testId={`${testPrefix}-concept`}
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Pertenece a</label>
        <MemberSelect value={form.member_id} onChange={(id) => setForm({ ...form, member_id: id })} preselectCurrent={testPrefix === "new"} testId={`${testPrefix}-member`} />
      </div>
      <MoneyMovementFields direction="out" values={form} onChange={(patch) => setForm({ ...form, ...patch })} testPrefix={testPrefix} required />
      <div>
        <label className="text-xs text-muted-foreground">Descripción / Observaciones</label>
        <Input data-testid={`${testPrefix}-comment`} placeholder="Nota opcional" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
      </div>
      <Button data-testid={`${testPrefix}-submit`} onClick={onSubmit} disabled={disabled} className="w-full h-11">{submitLabel}</Button>
      {disabled && <p className="text-[11px] text-muted-foreground">Requiere importe positivo, familia y método de pago.</p>}
    </div>
  );
}

export default function Gastos() {
  const [items, setItems] = useState(null);
  const [members, setMembers] = useState([]);
  const [cats, setCats] = useState([]);
  const [providers, setProviders] = useState([]);
  const [methods, setMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [audit, setAudit] = useState(null);
  const [filters, setFilters] = useState({ category: "", provider_id: "", payment_method_id: "", account_id: "", date_from: "", date_to: "", amount_min: "", amount_max: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [hasPin, setHasPin] = useState(false);

  const loadAll = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
    api.get("/expenses", { params }).then((r) => setItems(r.data));
    api.get("/expenses/summary", { params }).then((r) => setSummary(r.data));
  };
  useEffect(() => {
    api.get("/family").then((r) => setMembers(r.data.members || []));
    api.get("/categories?kind=gasto").then((r) => setCats(r.data));
    api.get("/providers").then((r) => setProviders(r.data));
    api.get("/payment-methods").then((r) => setMethods(r.data));
    api.get("/assets").then((r) => setAccounts(r.data));
    api.get("/auth/me").then((r) => setHasPin(!!r.data.has_pin));
  }, []);
  useEffect(loadAll, [filters]);

  const providerName = (id) => providers.find((p) => p.id === id)?.name;
  const methodName = (id) => methods.find((m) => m.id === id)?.name;
  const accountName = (id) => accounts.find((a) => a.id === id)?.name;

  const buildPayload = (f) => ({
    date: f.date,
    amount: parseFloat(String(f.amount).replace(",", ".")) || 0,
    category: f.category || "",
    subcategory: f.subcategory || null,
    concept_id: f.concept_id || null,
    account_id: f.account_id || null,
    provider_id: f.provider_id || null,
    payment_method_id: f.payment_method_id || null,
    comment: f.comment || null,
    member_id: f.member_id || null,
  });

  const submit = async () => {
    try {
      await api.post("/expenses", buildPayload(form));
      toast.success("Gasto registrado");
      setForm(EMPTY_FORM);
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/expenses/${edit.id}`, buildPayload(edit));
      toast.success("Gasto actualizado");
      setEdit(null);
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    try {
      await api.post(`/expenses/${del.id}/delete`, { pin: del.pin || "" });
      toast.success("Gasto eliminado");
      setDel(null);
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const duplicate = (g) => {
    setForm({
      date: todayISO(),
      amount: String(g.amount),
      category: g.category || "",
      subcategory: g.subcategory || "",
      concept_id: g.concept_id || "",
      provider_id: g.provider_id || "",
      payment_method_id: g.payment_method_id || "",
      account_id: g.account_id || "",
      comment: g.comment || "",
      member_id: g.member_id || null,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Datos copiados al formulario");
  };

  const openEdit = (g) => setEdit({ ...g, amount: String(g.amount), subcategory: g.subcategory || "", concept_id: g.concept_id || "", provider_id: g.provider_id || "", payment_method_id: g.payment_method_id || "", account_id: g.account_id || "", comment: g.comment || "" });

  const clearFilters = () => setFilters({ category: "", provider_id: "", payment_method_id: "", account_id: "", date_from: "", date_to: "", amount_min: "", amount_max: "" });
  const activeFiltersCount = useMemo(() => Object.values(filters).filter((v) => v !== "" && v !== null).length, [filters]);
  const topCats = cats.filter((c) => !c.parent_id && c.active);

  const openAudit = async () => {
    try {
      const r = await api.get("/expenses/audit");
      setAudit(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="gastos-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Gastos</h1>
          <p className="text-sm text-muted-foreground mt-1">Registra, edita y analiza cada gasto con proveedor y método de pago.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="open-audit" onClick={openAudit}><ClipboardList size={14} className="mr-1" /> Auditoría</Button>
        </div>
      </div>

      {/* Formulario de creación */}
      <div className="border border-border rounded-lg p-5" data-testid="expense-form-card">
        <div className="font-heading font-bold text-sm uppercase tracking-wider mb-4">Nuevo gasto</div>
        <ExpenseForm form={form} setForm={setForm} cats={cats} providers={providers} methods={methods} accounts={accounts}
          onProviderCreated={(p) => setProviders((prev) => [...prev, p].sort((a, b) => a.name.localeCompare(b.name)))}
          onSubmit={submit} submitLabel="Añadir gasto" testPrefix="new" />
      </div>

      {/* Resumen colapsable */}
      <div className="border border-border rounded-lg" data-testid="summary-card">
        <button onClick={() => setShowSummary(!showSummary)} className="w-full flex items-center justify-between p-4 text-left">
          <span className="font-heading font-bold text-sm uppercase tracking-wider">Resumen del período filtrado</span>
          {showSummary ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showSummary && summary && (
          <div className="p-4 pt-0 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-secondary/50 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Total</div>
                <Money value={summary.total} testId="summary-total" className="block mt-0.5 text-lg font-semibold tabular" />
              </div>
              <div className="bg-secondary/50 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Nº gastos</div>
                <div className="mt-0.5 text-lg font-semibold tabular">{summary.count}</div>
              </div>
              <div className="bg-secondary/50 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Familias</div>
                <div className="mt-0.5 text-lg font-semibold tabular">{summary.by_category?.length || 0}</div>
              </div>
              <div className="bg-secondary/50 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Proveedores</div>
                <div className="mt-0.5 text-lg font-semibold tabular">{(summary.by_provider || []).filter((p) => p.key !== "__none__").length}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TopList title="Top familias" rows={(summary.by_category || []).slice(0, 5)} testId="top-categories" />
              <TopList title="Top proveedores" rows={(summary.by_provider || []).slice(0, 5)} testId="top-providers" />
              <TopList title="Por método de pago" rows={(summary.by_payment_method || []).slice(0, 5)} testId="top-methods" />
              <TopList title="Por cuenta origen" rows={(summary.by_account || []).slice(0, 5)} testId="top-accounts" />
            </div>
            <a href="/analitica" data-testid="link-to-analitica" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">Ver analítica completa →</a>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="border border-border rounded-lg" data-testid="filters-card">
        <button onClick={() => setShowFilters(!showFilters)} className="w-full flex items-center justify-between p-4 text-left">
          <span className="font-heading font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            <Filter size={14} /> Filtros
            {activeFiltersCount > 0 && <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-bold">{activeFiltersCount}</span>}
          </span>
          {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showFilters && (
          <div className="p-4 pt-0 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Desde</label>
              <Input data-testid="f-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Hasta</label>
              <Input data-testid="f-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Familia</label>
              <Select value={filters.category || "all"} onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="f-cat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {topCats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Miembro</label>
              <Select value={filters.member_id || "all"} onValueChange={(v) => setFilters({ ...filters, member_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="f-member"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="comun">Común (familiar)</SelectItem>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Proveedor</label>
              <Select value={filters.provider_id || "all"} onValueChange={(v) => setFilters({ ...filters, provider_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="f-prov"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Método</label>
              <Select value={filters.payment_method_id || "all"} onValueChange={(v) => setFilters({ ...filters, payment_method_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="f-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {methods.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Cuenta</label>
              <Select value={filters.account_id || "all"} onValueChange={(v) => setFilters({ ...filters, account_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="f-account"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Importe mín (€)</label>
              <Input data-testid="f-min" inputMode="decimal" value={filters.amount_min} onChange={(e) => setFilters({ ...filters, amount_min: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Importe máx (€)</label>
              <Input data-testid="f-max" inputMode="decimal" value={filters.amount_max} onChange={(e) => setFilters({ ...filters, amount_max: e.target.value })} />
            </div>
            {activeFiltersCount > 0 && (
              <div className="col-span-2 md:col-span-4">
                <Button variant="outline" size="sm" data-testid="clear-filters" onClick={clearFilters}><X size={13} className="mr-1" /> Limpiar filtros</Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lista */}
      <div className="border border-border rounded-lg divide-y divide-border" data-testid="expenses-list">
        {items === null ? (
          <div className="p-5 text-sm text-muted-foreground">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Sin gastos con los filtros actuales.</div>
        ) : items.slice(0, 200).map((g) => (
          <div key={g.id} className="p-4 hover:bg-secondary/50 transition-colors" data-testid={`expense-row-${g.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{g.concept_name || providerName(g.provider_id) || <span className="italic text-muted-foreground">Sin concepto</span>}</span>
                  <span className="text-xs bg-secondary px-1.5 py-0.5 rounded font-semibold">{(g.concept_path || []).length > 0 ? g.concept_path.join(" › ") : (g.category || "Sin agrupación")}</span>
                  <MemberBadge memberId={g.member_id} />
                  {g.concept_name && providerName(g.provider_id) && <span className="text-xs text-muted-foreground">· {providerName(g.provider_id)}</span>}
                  {!g.concept_name && g.subcategory && <span className="text-xs text-muted-foreground">· {g.subcategory}</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 flex-wrap">
                  <span>{fmtDate(g.date)}</span>
                  {g.payment_method_id && <span>· {methodName(g.payment_method_id)}</span>}
                  {g.account_id && <span>· {accountName(g.account_id)}</span>}
                  {g.comment && <span className="truncate">· {g.comment}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Money value={g.amount} className="font-semibold tabular" />
                <button data-testid={`edit-expense-${g.id}`} onClick={() => openEdit(g)} title="Editar" className="text-muted-foreground hover:text-foreground"><Pencil size={14} /></button>
                <button data-testid={`duplicate-expense-${g.id}`} onClick={() => duplicate(g)} title="Duplicar" className="text-muted-foreground hover:text-foreground"><Copy size={14} /></button>
                <button data-testid={`delete-expense-${g.id}`} onClick={() => setDel({ ...g, pin: "" })} title="Eliminar" className="text-muted-foreground hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Editar */}
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Editar gasto</DialogTitle></DialogHeader>
          {edit && (
            <ExpenseForm form={edit} setForm={setEdit} cats={cats} providers={providers} methods={methods} accounts={accounts}
              onProviderCreated={(p) => setProviders((prev) => [...prev, p].sort((a, b) => a.name.localeCompare(b.name)))}
              onSubmit={saveEdit} submitLabel="Guardar cambios" testPrefix="edit" />
          )}
          {edit && <MovementTrace doc={edit} />}
        </DialogContent>
      </Dialog>

      {/* Eliminar con PIN */}
      <Dialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ShieldAlert size={16} className="text-red-600" /> Confirmar eliminación</DialogTitle></DialogHeader>
          {del && (
            <div className="space-y-3">
              <p className="text-sm">
                Vas a eliminar el gasto de <b><Money value={del.amount} /></b> del <b>{fmtDate(del.date)}</b>
                {del.provider_id ? ` a ${providerName(del.provider_id)}` : ""}. Esta acción queda registrada en la auditoría.
              </p>
              {hasPin ? (
                <div>
                  <label className="text-xs text-muted-foreground">Introduce tu PIN secundario para confirmar</label>
                  <Input data-testid="delete-pin-input" type="password" inputMode="numeric" maxLength={4} value={del.pin} onChange={(e) => setDel({ ...del, pin: e.target.value })} autoFocus onKeyDown={(e) => e.key === "Enter" && doDelete()} />
                </div>
              ) : (
                <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 rounded p-2">No tienes PIN secundario configurado. Configúralo desde el modo Privacidad para proteger las eliminaciones.</p>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDel(null)}>Cancelar</Button>
                <Button variant="destructive" className="flex-1" data-testid="confirm-delete-btn" onClick={doDelete} disabled={hasPin && (del.pin || "").length !== 4}>Eliminar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Auditoría */}
      <Dialog open={!!audit} onOpenChange={(v) => !v && setAudit(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Auditoría de gastos</DialogTitle></DialogHeader>
          {audit && audit.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Sin registros aún.</div>}
          {audit && audit.length > 0 && (
            <div className="divide-y divide-border" data-testid="audit-list">
              {audit.map((a) => (
                <div key={a.id} className="py-2 text-xs" data-testid={`audit-${a.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className={`font-bold uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded ${a.action === "delete" ? "bg-red-100 text-red-700" : a.action === "update" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{a.action}</span>
                    <span className="text-muted-foreground">{new Date(a.at).toLocaleString("es-ES")}</span>
                    <span className="text-muted-foreground">{a.user_email}</span>
                  </div>
                  <div className="mt-1 text-muted-foreground">
                    <b><Money value={a.snapshot?.amount || 0} /></b> · {a.snapshot?.category || "—"} · {fmtDate(a.snapshot?.date)} {a.snapshot?.comment && ` · ${a.snapshot.comment}`}
                  </div>
                  {a.action === "update" && Object.keys(a.changes || {}).length > 0 && (
                    <div className="mt-1 text-[10px] bg-secondary/40 rounded p-1.5 space-y-0.5">
                      {Object.entries(a.changes).map(([field, ch]) => (
                        <div key={field}><b>{field}</b>: <span className="line-through text-muted-foreground">{JSON.stringify(ch.before)}</span> → {JSON.stringify(ch.after)}</div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TopList({ title, rows, testId }) {
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <div className="bg-secondary/30 rounded-lg p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">{title}</div>
      {rows.length === 0 ? <div className="text-xs text-muted-foreground italic">Sin datos</div> : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.key} className="text-xs">
              <div className="flex items-center justify-between">
                <span className="truncate">{r.label}</span>
                <Money value={r.total} className="font-semibold tabular" />
              </div>
              <div className="h-1 bg-background rounded-full mt-0.5 overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${(r.total / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
